import React, { useState, useEffect } from 'react'
import { StyleSheet, View, RefreshControl, Platform } from 'react-native'
import { Text } from 'react-native-paper'
import { StackScreenProps } from '@react-navigation/stack'
import { AppNavigatorParams } from '../../../navigation/types'
import { useTranslation } from 'react-i18next'
import { useNodeByIdQuery } from '../../../generated/graphql'
import { ScreenWrapper } from '../../../@ui/containers/grid/screen.wrapper'
import { FilterChips, FilterOption } from '../../../@ui/buttons/filter.chips'
import { useTheme } from '../../../hooks/theme.hook'
import Toast from 'react-native-root-toast'
import { UserStatsTab } from './tabs/user-stats.tab'
import { PerspectiveStatsTab } from './tabs/perspective-stats.tab'
import { ContributionStatsTab } from './tabs/contribution-stats.tab'
import { Row } from '../../../@ui'
import LoadingFullScreen from '../../../@ui/containers/grid/full-screen.spinner'

type StatsTabType = 'users' | 'perspectives' | 'contributions'

function NodeStatsScreen({
  route,
  navigation,
}: StackScreenProps<AppNavigatorParams, 'NodeStats'>) {
  const { nodeId } = route.params
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [selectedTab, setSelectedTab] = useState<StatsTabType>('users')

  const { data, loading } = useNodeByIdQuery({
    variables: { id: nodeId },
    skip: !nodeId,
  })

  const node = data?.nodeById

  useEffect(() => {
    if (loading) return

    if (!node) {
      navigation.goBack()
      Toast.show(t('no-node-selected'), {
        duration: Toast.durations.LONG,
        position: Toast.positions.CENTER,
      })
      return
    }

    if (!node?.isAdmin) {
      navigation.goBack()
      Toast.show(t('node-stats-not-authorized'), {
        duration: Toast.durations.LONG,
        position: Toast.positions.CENTER,
      })
      return
    }
  }, [node, loading, nodeId])

  const tabOptions: FilterOption<StatsTabType>[] = [
    { value: 'users', label: t('user-stats') },
    { value: 'perspectives', label: t('perspective-stats') },
    { value: 'contributions', label: t('contribution-stats') },
  ]

  if (loading) {
    return <LoadingFullScreen />
  }

  if (!node) {
    return null
  }

  return (
    <ScreenWrapper withScrollView={false}>
      <View style={styles.headerSection}>
        <Row justifyContent="center" alignItems="center">
          <Text variant="headlineSmall" style={styles.title}>
            {t('node-stats-for', { nodeName: node.name })}
          </Text>
        </Row>
        <View style={styles.spacer} />
        <Row justifyContent="center" alignItems="center">
          <FilterChips
            options={tabOptions}
            selectedValue={selectedTab}
            onValueChange={setSelectedTab}
          />
        </Row>
      </View>

      <View style={styles.tabContainer}>
        {selectedTab === 'users' && (
          <UserStatsTab nodeId={nodeId} nodeName={node?.name || ''} />
        )}
        {selectedTab === 'perspectives' && (
          <PerspectiveStatsTab nodeId={nodeId} nodeName={node?.name || ''} />
        )}
        {selectedTab === 'contributions' && (
          <ContributionStatsTab nodeId={nodeId} nodeName={node?.name || ''} />
        )}
      </View>
    </ScreenWrapper>
  )
}

export default NodeStatsScreen

const styles = StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerSection: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  spacer: {
    height: 16,
  },
  tabContainer: {
    flex: 1,
  },
  title: {
    fontWeight: '600',
    textAlign: 'center',
  },
})
